import React from "react";

// ─── Utility helpers ────────────────────────────────────────────────────────

/** Returns true only for real, non-empty, non-"Invalid Date" strings */
const isValidString = (val) =>
  val !== null &&
  val !== undefined &&
  typeof val === "string" &&
  val.trim() !== "" &&
  val.trim().toLowerCase() !== "invalid date";

/** Joins name parts that are valid strings */
const buildFullName = (...parts) =>
  parts.filter(isValidString).join(" ").trim();

/** Returns value only if it's a valid, non-zero string/number */
const isValidValue = (val) => {
  if (val === null || val === undefined) return false;
  if (typeof val === "number") return val !== 0;
  if (typeof val === "string") return isValidString(val);
  return false;
};

/** Returns true if BOTH from and to times are valid */
const isValidTimeRange = (from, to) => isValidString(from) && isValidString(to);

/** Safe atob decoder — returns null on failure */
const safeAtob = (encoded) => {
  if (!isValidString(encoded)) return null;
  const trimmed = encoded.trim();
  if (
    trimmed.startsWith("<") ||
    trimmed.includes("</div>") ||
    trimmed.includes("</p>") ||
    trimmed.includes("</span>") ||
    trimmed.includes("</table>")
  ) {
    return trimmed.replace(/className=/g, "class=");
  }
  try {
    const clean = trimmed.replace(/\s/g, "");
    const binary = atob(clean);
    try {
      const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
      return new TextDecoder().decode(bytes).replace(/className=/g, "class=");
    } catch {
      return binary.replace(/className=/g, "class=");
    }
  } catch {
    return null;
  }
};

const escapeHtml = (str) =>
  String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

// Replaces {{token}} with escaped patient values; unknown/empty tokens become ""
const applyPlaceholders = (html, data = {}) => {
  if (!html) return "";
  return html.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, key) => {
    if (isValidValue(data[key])) {
      return escapeHtml(data[key]);
    }
    const lowerKey = key.toLowerCase();
    const matchedKey = Object.keys(data || {}).find(
      (k) => k.toLowerCase() === lowerKey
    );
    if (matchedKey && isValidValue(data[matchedKey])) {
      return escapeHtml(data[matchedKey]);
    }
    return "";
  });
};

// ─── Sub-components ──────────────────────────────────────────────────────────

const ClinicLogo = ({ logoName, logoB64 }) => {
  let src = null;
  if (isValidString(logoName)) {
    src =
      logoName.startsWith("http") || logoName.startsWith("data:")
        ? logoName
        : `${process.env.REACT_APP_IMAGE_URL || ""}${logoName}`;
  } else if (isValidString(logoB64)) {
    src = `data:image/jpeg;base64,${logoB64}`;
  }

  if (!src) return <span>&nbsp;</span>;
  return (
    <img
      src={src}
      alt="Clinic Logo"
      className="md:w-24 w-12 md:h-24 h-12 mx-4"
      onError={(e) => (e.currentTarget.style.display = "none")} // hide if image fails to load
    />
  );
};

const TimingRow = ({ label, from, to }) => {
  if (!isValidTimeRange(from, to)) return null;
  return (
    <p className="text-sm text-[#A7527E] font-medium mt-4">
      {label}:
      <span className="text-[#184B67]">{` ${from} - ${to} `}</span>
    </p>
  );
};

const ClinicInfo = ({ clinicDetails, hospitalAccountData }) => {
  const account = Array.isArray(hospitalAccountData)
    ? hospitalAccountData[0]
    : hospitalAccountData;

  return (
    <div>
      <div className="flex items-center">
        <ClinicLogo logoName={account?.logoName} logoB64={account?.logoB64} />
        {isValidString(clinicDetails?.clinic_Name) && (
          <div className="text-center mt-3">
            <label className="md:text-4xl text-sm font-serif font-medium text-[#154A6B]">
              {clinicDetails.clinic_Name}
            </label>
          </div>
        )}
      </div>

      <div className="md:flex gap-3">
        <TimingRow
          label="Morning"
          from={clinicDetails?.morningFromTime}
          to={clinicDetails?.morningToTime}
        />
        <TimingRow
          label="Evening"
          from={clinicDetails?.eveningFromTime}
          to={clinicDetails?.eveningToTime}
        />
      </div>
    </div>
  );
};

const DoctorInfo = ({ doctorData }) => {
  const fullName = buildFullName(
    doctorData?.salutation || doctorData?.userSalutation,
    doctorData?.firstName || doctorData?.userFirstName,
    doctorData?.lastName || doctorData?.userLastName
  );

  return (
    <div className="text-right text-[#184B67]">
      {isValidString(fullName) && (
        <label className="md:text-2xl text-sm font-bold text-[#244D67]">
          {fullName}
        </label>
      )}
      <p className="text-[#244D67] font-medium">
        {isValidString(doctorData?.qualification) && (
          <>
            <span className="text-xs">{doctorData.qualification}</span>
            <br />
          </>
        )}
        {isValidString(doctorData?.speciality) && (
          <>
            <span className="text-xs font-medium text-[#244D67]">
              {doctorData.speciality}
            </span>
            <br />
          </>
        )}
        {isValidString(doctorData?.registrationNumber) && (
          <span>{`Reg.no. ${doctorData.registrationNumber}`}</span>
        )}
      </p>
    </div>
  );
};

// ─── Main Component ──────────────────────────────────────────────────────────

function PrescriptionHeader({
  clinicDetails,
  doctorData,
  hospitalAccountData,
  patientInfo,
}) {
  const headerType =
    clinicDetails?.prescription_HeaderYN ||
    doctorData?.prescription_HeaderYN;

  const customHeaderContent =
    doctorData?.headerDetails ||
    clinicDetails?.headerDetails ||
    clinicDetails?.prescription_Header_Details;

  const hasCustomHeader = isValidString(customHeaderContent);

  // Case 1 — Custom HTML header (base64 encoded)
  if ((headerType === "C" || (!headerType && hasCustomHeader)) && hasCustomHeader) {
    const decoded = safeAtob(customHeaderContent);
    if (!decoded) return null;
    const html = applyPlaceholders(decoded, patientInfo || {});
    return <div dangerouslySetInnerHTML={{ __html: html }} />;
  }

  // Case 2 — System-generated header ('Y') OR 'C' with no custom header fallback
  if (headerType === "Y" || (headerType === "C" && !hasCustomHeader)) {
    const showClinicInfo =
      clinicDetails?.clinicDetailsInPrescriptionYN === "Y" ||
      (headerType === "C" && !hasCustomHeader);

    return (
      <div className="border-b-2 border-gray-300 md:flex justify-between items-start pb-2">
        {showClinicInfo ? (
          <ClinicInfo
            clinicDetails={clinicDetails}
            hospitalAccountData={hospitalAccountData}
          />
        ) : (
          <div>&nbsp;</div>
        )}
        <DoctorInfo doctorData={doctorData} />
      </div>
    );
  }

  // Case 3 — No header, just reserve vertical space
  if (headerType === "N") {
    const space = isValidValue(clinicDetails?.prescription_Header_Space)
      ? clinicDetails.prescription_Header_Space
      : 0;
    return <div style={{ height: `${space}px` }} />;
  }

  // Case 4 — Unknown / missing headerType
  return null;
}

export default PrescriptionHeader;
